import { useCallback, useRef, useState } from 'react';
import useSSEStream from './useSSEStream';
import campaignService from '../services/campaignService';
import { EVENT_TYPES } from '../utils/streamEventRouter';

/**
 * Custom hook for orchestrating custom field operations
 * Handles generation, preview, and approval of custom fields
 * Bridges between CampaignChatInterface and VirtualizedPersonaResults
 * 
 * @param {string} campaignId - Campaign ID
 * @param {Function} onStatusUpdate - Callback for status updates
 * @param {Function} onDataUpdate - Callback for data updates (field definitions and values)
 * @param {Function} onComplete - Callback when operation completes
 * @param {Function} onError - Callback for errors
 * @returns {Object} - Custom field operations and state
 */
const useCustomFieldOrchestration = ({
  campaignId,
  onStatusUpdate,
  onDataUpdate,
  onComplete,
  onError
}) => {
  const customFieldOperationRef = useRef(null);
  const [refreshProgress, setRefreshProgress] = useState(null);

  const handleStatus = useCallback((status) => {
    const isRefresh = customFieldOperationRef.current === 'refresh';

    if (isRefresh) {
      const pct = parseInt(status.completion_percentage) || 0;
      const completed = parseInt(status.completed) || 0;
      const total = parseInt(status.total) || 0;

      setRefreshProgress((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          aggregated: { completed, total, percentage: pct },
        };
      });
    }

    if (onStatusUpdate) {
      onStatusUpdate(status, 'custom_fields');
    }
    if (onDataUpdate) {
      onDataUpdate({ type: 'status', data: status });
    }
  }, [onStatusUpdate, onDataUpdate]);

  // SSE Stream for custom field generation
  const { startStream, stopStream } = useSSEStream({
    onStatus: handleStatus,
    onData: (data) => {
      if (onDataUpdate) {
        if (data.event === EVENT_TYPES.CUSTOM_FIELD_DEFINITION) {
          onDataUpdate({
            type: 'custom_field_definition',
            data: data.data
          });
        } else if (data.event === EVENT_TYPES.COMPANIES_CUSTOM_FIELD_UPDATE ||
                   data.event === EVENT_TYPES.CONTACTS_CUSTOM_FIELD_UPDATE ||
                   data.event === EVENT_TYPES.SEC_FILINGS_CUSTOM_FIELD_UPDATE) {
          onDataUpdate({
            type: 'custom_field_update',
            data: data.data
          });
        }
      }
    },
    onComplete: async () => {
      const operationType = customFieldOperationRef.current;
      customFieldOperationRef.current = null;

      if (operationType === 'refresh') {
        setRefreshProgress((prev) =>
          prev
            ? { ...prev, aggregated: { ...prev.aggregated, percentage: 100 } }
            : prev
        );
      }
      
      if (onComplete) {
        await onComplete(operationType);
      }
    },
    onError: (error) => {
      customFieldOperationRef.current = null;
      setRefreshProgress(null);
      if (onError) {
        onError(error);
      }
    },
  });

  /**
   * Generate custom fields with preview
   * 
   * @param {string} prompt - Generation prompt
   * @param {string} configId - Config ID for iteration (optional)
   */
  const generateFields = useCallback(async (prompt, configId = null, entityType = 'companies') => {
    if (!prompt || !campaignId) return;
    
    // Mark this as a preview operation
    customFieldOperationRef.current = 'preview';
    
    // Start SSE stream for custom field preview generation
    await startStream(
      `/api/custom-fields/campaigns/${campaignId}/fields/generate/stream`,
      {
        method: 'POST',
        body: {
          prompt: prompt,
          config_id: configId,
          entity_type: entityType,
        },
      }
    );
  }, [campaignId, startStream]);

  /**
   * Approve custom field preview and run full generation
   * 
   * @param {string} configId - Config ID to approve
   */
  const approvePreview = useCallback(async (configId) => {
    if (!configId || !campaignId) return;
    
    // Mark this as an approval operation
    customFieldOperationRef.current = 'approval';
    
    // Start SSE stream for custom field approval (full generation)
    await startStream(
      `/api/custom-fields/campaigns/${campaignId}/configs/${configId}/approve/stream`,
      {
        method: 'POST',
      }
    );
  }, [campaignId, startStream]);

  /**
   * Reject custom field preview
   * 
   * @param {string} configId - Config ID to reject
   */
  const rejectPreview = useCallback(async (configId) => {
    if (!configId || !campaignId) return;
    
    try {
      await campaignService.rejectCustomFieldPreview(campaignId, configId);
    } catch (error) {
      console.error('Error rejecting preview:', error);
      if (onError) {
        onError(error);
      }
    }
  }, [campaignId, onError]);

  /**
   * Refresh custom fields for new (un-enriched) rows.
   *
   * @param {string[]} fieldDefinitionIds - Selected field definition IDs
   */
  const refreshFields = useCallback(async (fieldDefinitionIds) => {
    if (!fieldDefinitionIds?.length || !campaignId) return;

    customFieldOperationRef.current = 'refresh';
    setRefreshProgress({
      isRefreshing: true,
      aggregated: { completed: 0, total: 0, percentage: 0 },
      configs: [],
    });

    await startStream(
      `/api/custom-fields/campaigns/${campaignId}/refresh/stream`,
      {
        method: 'POST',
        body: { field_definition_ids: fieldDefinitionIds },
      }
    );
  }, [campaignId, startStream]);

  const clearRefreshProgress = useCallback(() => {
    setRefreshProgress(null);
  }, []);

  /**
   * Abort any in-flight SSE stream and reset operation state.
   * Called by the parent when the user clicks Stop so the custom-field
   * SSE connection is torn down immediately alongside the chat stream.
   */
  const cancelOperation = useCallback(() => {
    customFieldOperationRef.current = null;
    setRefreshProgress(null);
    stopStream();
  }, [stopStream]);

  return {
    generateFields,
    approvePreview,
    rejectPreview,
    refreshFields,
    refreshProgress,
    clearRefreshProgress,
    cancelOperation,
    isGenerating: customFieldOperationRef.current !== null,
    currentOperation: customFieldOperationRef.current,
  };
};

export default useCustomFieldOrchestration;

